import React from 'react';
import ProgressHeader from './ProgressHeader';
import ContinueButton from './common/ContinueButton';
import {
  GENDERS,
  CLASSES,
  DIFFICULTIES,
  BIRTH_YEARS,
} from '../constants/onboardingData';
import { CircleCheck } from 'lucide-react';

/**
 * Step 2: Informations sur l'enfant
 */
export default function Step2ChildInfo({
  formData,
  currentStepNumber,
  currentStepMessage,
  canContinue,
  validationMessage,
  onUpdateChildInfo,
  onToggleDifficulty,
  onContinue,
  onBack,
}) {
  return (
    <div className="min-h-[100dvh] bg-[#F0FDFA] flex flex-col">
      <ProgressHeader
        currentStep={currentStepNumber}
        message={currentStepMessage}
        showBack={true}
        onBack={onBack}
      />

      <div className="flex-1 px-4 pb-4 overflow-y-auto">
        {/* Titre */}
        <div className="text-center py-6">
          <h1 className="text-2xl font-bold text-[#1f2937] mb-2">
            Parlez-nous de votre enfant
          </h1>
          <p className="text-[#6b7280]">
            5 questions simples pour personnaliser son parcours
          </p>
        </div>

        {/* Question 1: Prénom de l'enfant */}
        <div className="bg-white rounded-2xl p-6 mb-4 shadow-lg">
          <div className="flex items-center gap-2 mb-4">
            <div className="rounded-lg bg-transparent flex items-center justify-center">
              <svg className="w-8 h-8 text-[#059669]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </div>
            <span className="font-bold text-lg text-[#1f2937]">1️⃣ Nom complet de l'enfant</span>
          </div>

          <input
            type="text"
            value={formData.childName || ''}
            onChange={(e) => onUpdateChildInfo('childName', e.target.value)}
            placeholder="Ex: Aminata Diallo"
            className="w-full p-3 border-2 border-[#e5e7eb] rounded-xl text-[#374151] 
                       focus:border-[#2DD4BF] focus:outline-none transition-colors"
          />
        </div>

        {/* Question 2: Genre */}
        <div className="bg-white rounded-2xl p-6 mb-4 shadow-lg">
          <div className="flex items-center gap-2 mb-4">
            <div className="rounded-lg bg-transparent flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" 
              className="lucide lucide-user-icon lucide-user w-8 h-8 text-[#059669]"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
            </div>
            <span className="font-bold text-lg text-[#1f2937]">2️⃣ Garçon ou fille ?</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {GENDERS.map((gender) => (
              <div
                key={gender.id}
                onClick={() => onUpdateChildInfo('childGender', gender.id)}
                className={`
                  p-4 rounded-xl cursor-pointer transition-all text-center
                  ${formData.childGender === gender.id
                    ? 'border-[#10b981] bg-[#F0FDFA] hover:border-[4px] border-[3px]'
                    : 'border-[#e5e7eb] hover:border-[#d1d5db] border-[2px]'
                  }
                `}
              >
                <span className="text-3xl block mb-1">{gender.emoji}</span>
                <span className="font-medium text-[#374151]">{gender.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Question 3: Année de naissance */}
        <div className="bg-white rounded-2xl p-6 mb-4 shadow-lg">
          <div className="flex items-center gap-2 mb-4">
            <div className="rounded-lg bg-transparent flex items-center justify-center">
              <svg className="w-8 h-8 text-[#059669]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <span className="font-bold text-[#1f2937] text-lg">3️⃣ Année de naissance</span>
          </div>

          <select
            value={formData.childBirthYear || ''}
            onChange={(e) => onUpdateChildInfo('childBirthYear', parseInt(e.target.value))}
            className="w-full p-3 border-2 border-[#e5e7eb] rounded-xl text-[#374151] 
                       focus:border-[#2DD4BF] focus:outline-none transition-colors"
          >
            <option value="">Sélectionnez une année</option>
            {BIRTH_YEARS.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {/* Question 4: Classe */}
        <div className="bg-white rounded-2xl p-6 mb-4 shadow-lg">
          <div className="flex items-center gap-2 mb-4">
            <div className="rounded-lg bg-transparent flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" 
              className="lucide lucide-school-icon lucide-school text-[#059669] w-8 h-8"><path d="M14 21v-3a2 2 0 0 0-4 0v3"/><path d="M18 5v16"/><path d="m4 6 7.106-3.79a2 2 0 0 1 1.788 0L20 6"/><path d="m6 11-3.52 2.147a1 1 0 0 0-.48.854V19a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-5a1 1 0 0 0-.48-.853L18 11"/><path d="M6 5v16"/><circle cx="12" cy="9" r="2"/>
              </svg>
            </div>
            <span className="font-bold text-[#1f2937] text-lg">4️⃣ Classe actuelle</span>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {CLASSES.map((classe) => (
              <div
                key={classe}
                onClick={() => onUpdateChildInfo('childClass', classe)}
                className={`
                  py-2 px-3 rounded-lg cursor-pointer transition-all text-center text-sm font-bold
                  ${formData.childClass === classe
                    ? 'bg-[#D1FAE5] text-[#047857]'
                    : ' text-[#047857]'
                  }
                `}
              >
                {classe}
              </div>
            ))}
          </div>
        </div>

        {/* Question 5: Difficultés */}
        <div className="bg-white rounded-2xl p-6 mb-4 shadow-lg">
          <div className="flex items-center gap-2 mb-2">
            <div className="rounded-lg bg-transparent flex items-center justify-center">
              <svg className="w-8 h-8 text-[#059669]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <span className="font-bold text-[#1f2937] text-lg">5️⃣ Difficulté particulière ?</span>
          </div>
          <p className="text-[#6b7280] text-sm mb-4 ml-10">
            Vous pouvez choisir plusieurs réponses
          </p>

          <div className="grid grid-cols-2 gap-3">
            {DIFFICULTIES.map((difficulty) => (
              <div
                key={difficulty.id}
                onClick={() => onToggleDifficulty(difficulty.id)}
                className={`
                  p-3 rounded-xl cursor-pointer transition-all text-center relative
                  ${formData.childDifficulties.includes(difficulty.id)
                    ? 'border-[#10b981] bg-[#ECFDF5] border-[3px]'
                    : 'border-[#e5e7eb] hover:border-[#d1d5db] border-[2px]'
                  }
                `}
              >
                {formData.childDifficulties.includes(difficulty.id) && <CircleCheck className='absolute right-1 text-white top-1' size={30} fill={"#059669"} /> }
                <span className="text-3xl block mb-1">{difficulty.emoji}</span>
                <span className="font-medium text-[#374151] text-md">{difficulty.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bouton Continuer */}
      <div className="sticky bottom-0 bg-[#F0FDFA] p-4 border-t border-[#f3f4f6]">
        {validationMessage && !canContinue && (
          <p className="text-center text-sm text-[#6b7280] mb-2">{validationMessage}</p>
        )}
        <ContinueButton
          onClick={onContinue}
          disabled={!canContinue}
        />
      </div>
    </div>
  );
}
